// app/src/lib/cashflow/forecast.js — 13-week cash flow forecast (US-3.4).
//
// Algorithm:
//   1. Opening balance = sum of the cash accounts' currentBalanceCents
//      (position.selectCashAccounts: live depository balances, with the manual
//      CSV-snapshot account superseded once a live one exists).
//   2. For each of 13 weekly buckets (Monday-start) build:
//        outflows = sum(invoices.dueDate in week, status open)
//                 + sum(recurring items occurring in week)
//                 + sum(payrollEvents in week)
//        inflows  = sum(ar.expectedDate in week, status OPEN/OVERDUE)
//                 + sales rolling 4-week average projected forward, scaled by
//                   LAST YEAR's monthly pattern (see "Seasonal projection")
//   3. closing = opening + (inflows - outflows); next week opens with that closing.
//
// Seasonal projection: a flat rolling average draws a straight line through 13
// weeks, which hides December-vs-January-sized swings. When at least ~2 weeks
// of sales exist one year back, each projected day is scaled by
//   factor(day) = lastYear(month of day−364) daily avg ÷ lastYear(baseline window) daily avg
// — i.e. "this time of year ran X× the current period's pace last year".
// 364 days (52 weeks) keeps weekday alignment. Factors clamp to [0.5, 2] and
// default to 1 wherever last year's data is missing or sparse, so a young
// account degrades gracefully to the flat average.
//
// Scenario adjustments (US-3.6) are applied on top — see scenarios.js.

import { db } from '../db.js';
import { vendorLabel } from '../demo.js';
import { requireUserId } from '../procurement/util.js';
import { addDays, isoToday, mondayOf } from './util.js';
import { selectCashAccounts } from './position.js';

const WEEKS = 13;
const SALES_LOOKBACK_WEEKS = 4;

/**
 * @typedef {Object} ForecastWeek
 * @property {string} weekStart        - ISO Monday.
 * @property {string} weekEnd          - ISO Sunday.
 * @property {number} openingCents
 * @property {number} inflowCents
 * @property {number} outflowCents
 * @property {number} netCents
 * @property {number} closingCents
 * @property {Array<{ source:string, label:string, dateIso:string, amountCents:number, kind:'in'|'out' }>} items
 */

/**
 * Build the forecast.
 * @param {{ adjustments?: import('./scenarios.js').Adjustment[] }} [opts]
 * @returns {Promise<{ asOf:string, weeks: ForecastWeek[], averageWeeklyOutflowCents:number }>}
 */
export async function build(opts = {}) {
  const userId = await requireUserId();
  const today = isoToday();
  const startMon = mondayOf(today);

  // Opening balance from current depository accounts — same selection rule as
  // the cash position: a manual CSV-snapshot account is superseded (dropped)
  // once any live Plaid depository balance exists, or both would be counted.
  const accounts = await db.plaidAccounts.where('userId').equals(userId).toArray();
  const openingCents = selectCashAccounts(accounts)
    .reduce((s, a) => s + (a.currentBalanceCents || 0), 0);

  // Pull source data once, then bucket per week.
  const invoices = await db.invoices.where('userId').equals(userId)
    .filter((i) => ['RECEIVED', 'PAID_LOCAL', 'OVERDUE', 'DISPUTED'].includes(i.status) && i.dueDate)
    .toArray();
  const recurring = await db.recurringItems.where('userId').equals(userId).toArray();
  const payrolls  = await db.payrollEvents.where('userId').equals(userId).toArray();
  const ars       = await db.arEntries.where('userId').equals(userId)
    .filter((a) => a.status === 'OPEN' || a.status === 'OVERDUE')
    .toArray();
  const vendors = new Map((await db.vendors.where('userId').equals(userId).toArray()).map((v) => [v.vendorId, v]));

  // Sales rolling 4-week average → the projection baseline.
  const salesLookbackStart = addDays(today, -SALES_LOOKBACK_WEEKS * 7);
  const salesRows = await db.salesEntries
    // Upper bound EXCLUSIVE by design: today is a partial day and would drag the
    // rolling average down (see forecast.test.js).
    .where('[userId+date]').between([userId, salesLookbackStart], [userId, today])
    .toArray();
  const salesTotal = salesRows.reduce((s, r) => s + (r.netSalesCents || 0), 0);
  const salesPerDay = SALES_LOOKBACK_WEEKS > 0 ? Math.round(salesTotal / (SALES_LOOKBACK_WEEKS * 7)) : 0;

  // ── Seasonal projection (see module comment) ──────────────────────────────
  const SEASONAL_SHIFT = 364; // 52 weeks back — keeps weekday alignment
  const MIN_BASELINE_DAYS = 14;
  const MIN_MONTH_DAYS = 7;
  const horizonEnd = addDays(startMon, WEEKS * 7 - 1);
  const lyRows = await db.salesEntries
    .where('[userId+date]')
    .between([userId, addDays(salesLookbackStart, -SEASONAL_SHIFT)], [userId, addDays(horizonEnd, -SEASONAL_SHIFT)])
    .toArray();
  const lyByMonth = new Map(); // 'YYYY-MM' → { cents, days:Set }
  const lyBase = { cents: 0, days: new Set() };
  const lyBaseEnd = addDays(today, -SEASONAL_SHIFT);
  for (const r of lyRows) {
    const n = r.netSalesCents || 0;
    const ym = r.date.slice(0, 7);
    let b = lyByMonth.get(ym);
    if (!b) { b = { cents: 0, days: new Set() }; lyByMonth.set(ym, b); }
    b.cents += n; b.days.add(r.date);
    if (r.date <= lyBaseEnd) { lyBase.cents += n; lyBase.days.add(r.date); }
  }
  // Per-OPEN-day averages (days with entries), so a closed Monday or a sparse
  // import doesn't drag a month down; only the ratio between the two matters.
  const lyBaseAvg = lyBase.days.size >= MIN_BASELINE_DAYS ? lyBase.cents / lyBase.days.size : 0;
  const seasonal = lyBaseAvg > 0;
  const seasonalFactor = (dayIso) => {
    if (!seasonal) return 1;
    const b = lyByMonth.get(addDays(dayIso, -SEASONAL_SHIFT).slice(0, 7));
    if (!b || b.days.size < MIN_MONTH_DAYS) return 1;
    return Math.min(2, Math.max(0.5, (b.cents / b.days.size) / lyBaseAvg));
  };

  // Apply scenario adjustments by transforming the source streams.
  const adj = opts.adjustments || [];
  const adjustedInvoices = applyInvoiceAdjustments(invoices, adj);
  const adjustedArs      = applyArAdjustments(ars, adj);
  const adHocEvents      = collectAdHocAdjustments(adj);

  const weeks = [];
  let runningOpening = openingCents;
  for (let i = 0; i < WEEKS; i++) {
    const weekStart = addDays(startMon, i * 7);
    const weekEnd = addDays(weekStart, 6);
    const items = [];
    let outflow = 0;
    let inflow = 0;

    // Invoices due in window.
    for (const inv of adjustedInvoices) {
      if (inv.dueDate >= weekStart && inv.dueDate <= weekEnd) {
        outflow += inv.totalCents || 0;
        items.push({
          source: 'invoice',
          label: `${vendorLabel(vendors.get(inv.vendorId)?.name, 'Vendor')} ${inv.invoiceNumber || ''}`.trim(),
          dateIso: inv.dueDate,
          amountCents: inv.totalCents || 0,
          kind: 'out',
        });
      }
    }

    // Recurring items: project the next occurrence(s) inside the window using cadence.
    for (const r of recurring) {
      for (const occurrence of occurrencesInWindow(r, weekStart, weekEnd)) {
        outflow += r.amountCents || 0;
        items.push({
          source: 'recurring',
          label: r.label || r.name || 'Recurring',
          dateIso: occurrence,
          amountCents: r.amountCents || 0,
          kind: 'out',
        });
      }
    }

    // Payroll events.
    for (const p of payrolls) {
      if (p.payDate >= weekStart && p.payDate <= weekEnd) {
        outflow += p.amountCents || 0;
        items.push({
          source: 'payroll',
          label: p.label || 'Payroll',
          dateIso: p.payDate,
          amountCents: p.amountCents || 0,
          kind: 'out',
        });
      }
    }

    // AR expected receipts.
    for (const a of adjustedArs) {
      if (a.expectedDate >= weekStart && a.expectedDate <= weekEnd) {
        inflow += a.amountCents || 0;
        items.push({
          source: 'ar',
          label: a.customerName,
          dateIso: a.expectedDate,
          amountCents: a.amountCents || 0,
          kind: 'in',
        });
      }
    }

    // Projected sales: salesPerDay per day, each day scaled by last year's
    // monthly pattern (factor 1 when no usable last-year data).
    let weekFactor = 1;
    if (salesPerDay > 0) {
      let projected = 0;
      let factorSum = 0;
      for (let d = 0; d < 7; d += 1) {
        const f = seasonalFactor(addDays(weekStart, d));
        factorSum += f;
        projected += Math.round(salesPerDay * f);
      }
      weekFactor = Math.round((factorSum / 7) * 100) / 100;
      inflow += projected;
      items.push({
        source: 'sales-projection',
        label: seasonal && weekFactor !== 1
          ? `Projected sales (rolling avg × ${weekFactor.toFixed(2)} seasonal)`
          : 'Projected sales (rolling avg)',
        dateIso: weekStart,
        amountCents: projected,
        kind: 'in',
      });
    }

    // Ad-hoc scenario events.
    for (const ev of adHocEvents) {
      if (ev.dateIso >= weekStart && ev.dateIso <= weekEnd) {
        if (ev.kind === 'in') inflow += ev.amountCents;
        else outflow += ev.amountCents;
        items.push({ ...ev, source: 'scenario' });
      }
    }

    const net = inflow - outflow;
    const closing = runningOpening + net;
    weeks.push({
      weekStart,
      weekEnd,
      openingCents: runningOpening,
      inflowCents: inflow,
      outflowCents: outflow,
      netCents: net,
      closingCents: closing,
      seasonalFactor: weekFactor,
      items,
    });
    runningOpening = closing;
  }

  const totalOut = weeks.reduce((s, w) => s + w.outflowCents, 0);
  const averageWeeklyOutflowCents = Math.round(totalOut / WEEKS);

  return { asOf: today, weeks, averageWeeklyOutflowCents, seasonal };
}

// ── Recurring cadence projection ──────────────────────────────────────────

function occurrencesInWindow(r, winStart, winEnd) {
  if (!r.nextDate) return [];
  const cadence = r.cadence || r.frequency;
  const out = [];
  // Fast-forward `cur` until it is >= winStart, even if nextDate is in the past
  // (e.g. nextDate hasn't been advanced since the last actual posting). Cap
  // iterations defensively to avoid pathological loops if cadence is invalid.
  let cur = r.nextDate;
  let safety = 0;
  while (cur < winStart && safety++ < 500) {
    const next = advanceByCadence(cur, cadence);
    if (!next || next === cur) return [];
    cur = next;
  }
  // Now collect every occurrence that falls inside [winStart, winEnd].
  while (cur <= winEnd && safety++ < 500) {
    out.push(cur);
    const next = advanceByCadence(cur, cadence);
    if (!next || next === cur) break;
    cur = next;
  }
  return out;
}

function advanceByCadence(iso, cadence) {
  const map = { WEEKLY: 7, BIWEEKLY: 14, MONTHLY: null, QUARTERLY: null, ANNUAL: null };
  if (cadence === 'WEEKLY' || cadence === 'weekly') return addDays(iso, 7);
  if (cadence === 'BIWEEKLY' || cadence === 'biweekly') return addDays(iso, 14);
  if (cadence === 'MONTHLY' || cadence === 'monthly') {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCMonth(d.getUTCMonth() + 1);
    return d.toISOString().slice(0, 10);
  }
  if (cadence === 'QUARTERLY' || cadence === 'quarterly') {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCMonth(d.getUTCMonth() + 3);
    return d.toISOString().slice(0, 10);
  }
  if (cadence === 'ANNUAL' || cadence === 'annual') {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCFullYear(d.getUTCFullYear() + 1);
    return d.toISOString().slice(0, 10);
  }
  return null;
}

// ── Adjustments (scenario overlay) ────────────────────────────────────────

function applyInvoiceAdjustments(invoices, adj) {
  if (!adj.length) return invoices;
  return invoices.map((inv) => {
    const matching = adj.find((a) => a.type === 'shiftInvoice' && a.invoiceId === inv.invoiceId);
    if (!matching) return inv;
    return { ...inv, dueDate: addDays(inv.dueDate, matching.deltaDays || 0) };
  });
}

function applyArAdjustments(ars, adj) {
  if (!adj.length) return ars;
  return ars.map((ar) => {
    const matching = adj.find((a) => a.type === 'delayAr' && a.arId === ar.arId);
    if (!matching) return ar;
    return { ...ar, expectedDate: addDays(ar.expectedDate, matching.deltaDays || 0) };
  });
}

function collectAdHocAdjustments(adj) {
  return adj
    .filter((a) => a.type === 'adHoc' && a.dateIso && Number.isFinite(a.amountCents))
    .map((a) => ({
      label: a.label || 'Scenario adjustment',
      dateIso: a.dateIso,
      amountCents: Math.abs(Math.round(a.amountCents)),
      kind: a.amountCents > 0 ? 'in' : 'out',
    }));
}
